/**
 * Super-admin · Assign Project.
 *
 * Pick a tenant → pick one of its projects → toggle which of that tenant's
 * people are assigned. Assignment drives the employee Rooms chat: assigned
 * employees automatically share the project's room.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type CSSProperties } from "react";

import { api, extractErrorMessage } from "@/lib/api";
import type { ProjectMemberRow, SuperProject } from "@/lib/types";

type TenantLite = { id: string; name: string };
type TenantList = { tenants: TenantLite[] };

export function AssignProjectPage() {
  const qc = useQueryClient();
  const [tenantId, setTenantId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const tenantsQuery = useQuery({
    queryKey: ["superadmin-tenants"],
    queryFn: async () => (await api.get<TenantList>("/superadmin/tenants")).data,
  });
  const tenants = tenantsQuery.data?.tenants ?? [];

  const projectsQuery = useQuery({
    queryKey: ["superadmin-projects", tenantId],
    queryFn: async () =>
      (await api.get<SuperProject[]>(`/superadmin/projects?tenant_id=${tenantId}`)).data,
    enabled: !!tenantId,
  });
  const projects = projectsQuery.data ?? [];

  // When a tenant is picked, auto-select its assigned project (the common case —
  // one project per tenant) so it appears without a manual second click. Runs
  // once per tenant; the user can still change it. Unassigned projects (tenant_id
  // null) are skipped, so a just-unassigned project won't auto-reselect.
  const autoSelectedFor = useRef<string>("");
  useEffect(() => {
    if (tenantId && autoSelectedFor.current !== tenantId && projects.length > 0) {
      const assigned = projects.filter((p) => p.tenant_id === tenantId);
      if (assigned.length > 0) {
        setProjectId(assigned[0].id);
        autoSelectedFor.current = tenantId;
      }
    }
  }, [tenantId, projects]);

  const membersQuery = useQuery({
    queryKey: ["project-members", projectId],
    queryFn: async () =>
      (await api.get<ProjectMemberRow[]>(`/superadmin/projects/${projectId}/members`)).data,
    enabled: !!projectId,
  });
  const members = membersQuery.data ?? [];

  const selectedProject = projects.find((p) => p.id === projectId);
  const isUnbound = !!selectedProject && selectedProject.tenant_id == null;
  const selectedTenantName = tenants.find((t) => t.id === tenantId)?.name ?? "this tenant";

  // Bind a tenant-less project to the chosen tenant — this creates its backlog
  // tickets from the stored tasks.xlsx and lets the team be assigned.
  const bind = useMutation({
    mutationFn: async () =>
      (await api.post(`/superadmin/projects/${projectId}/tenant`, { tenant_id: tenantId })).data,
    onMutate: () => setError(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["superadmin-projects", tenantId] });
      qc.invalidateQueries({ queryKey: ["project-members", projectId] });
    },
    onError: (err) =>
      setError(extractErrorMessage(err, "Could not assign the project to this tenant.")),
  });

  // Remove the project from its tenant: stops Atlas + hides the backlog (statuses
  // preserved). Re-assigning restores it and resumes from the remaining tickets.
  const unassign = useMutation({
    mutationFn: async () =>
      (await api.post(`/superadmin/projects/${projectId}/unassign`)).data,
    onMutate: () => setError(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["superadmin-projects", tenantId] });
      qc.invalidateQueries({ queryKey: ["project-members", projectId] });
      setProjectId("");
    },
    onError: (err) => setError(extractErrorMessage(err, "Could not remove the project.")),
  });

  const toggle = useMutation({
    mutationFn: async (vars: { userId: string; assign: boolean }) => {
      if (vars.assign) {
        await api.post(`/superadmin/projects/${projectId}/members`, { user_id: vars.userId });
      } else {
        await api.delete(`/superadmin/projects/${projectId}/members/${vars.userId}`);
      }
    },
    onMutate: () => setError(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-members", projectId] });
      qc.invalidateQueries({ queryKey: ["superadmin-projects", tenantId] });
    },
    onError: (err) => setError(extractErrorMessage(err, "Could not update the assignment.")),
  });

  return (
    <div style={{ maxWidth: 760 }}>
      <header style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          Manage projects
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          Choose a tenant and a project, then assign its people — or remove the
          project from the tenant to pause it. Re-assigning resumes from the
          remaining backlog with the newly-assigned team.
        </p>
      </header>

      <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
        <label style={{ ...field, minWidth: 240 }}>
          <span style={label}>Tenant</span>
          <select
            value={tenantId}
            onChange={(e) => {
              setTenantId(e.target.value);
              setProjectId("");
            }}
            style={input}
          >
            <option value="">Select a tenant…</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </label>

        <label style={{ ...field, minWidth: 240 }}>
          <span style={label}>Project</span>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            style={input}
            disabled={!tenantId}
          >
            <option value="">{tenantId ? "Select a project…" : "Pick a tenant first"}</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.key} — {p.name}
                {p.tenant_id == null ? " (unassigned)" : ""}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <div role="alert" style={errBar}>{error}</div>}

      {!projectId ? (
        <p style={{ color: "var(--mute)" }}>Select a tenant and project to manage assignments.</p>
      ) : isUnbound ? (
        <div style={bindCard}>
          <p style={{ margin: 0, fontSize: 13.5, color: "var(--body-mid)" }}>
            <strong>{selectedProject?.key}</strong> isn't assigned to a tenant yet.
            Assign it to <strong>{selectedTenantName}</strong> to create its backlog
            from the uploaded tasks and add the team.
          </p>
          <button
            type="button"
            onClick={() => bind.mutate()}
            disabled={bind.isPending}
            style={primaryBtn(bind.isPending)}
          >
            {bind.isPending ? "Assigning…" : `Assign to ${selectedTenantName} & create backlog`}
          </button>
        </div>
      ) : (
        <>
          {membersQuery.isLoading ? (
            <p style={{ color: "var(--mute)" }}>Loading members…</p>
          ) : members.length === 0 ? (
            <p style={{ color: "var(--mute)" }}>This tenant has no members yet.</p>
          ) : (
            <div style={{ border: "1px solid var(--hairline)", borderRadius: 8, overflow: "hidden" }}>
              {members.map((m) => (
                <label key={m.user_id} style={row}>
                  <input
                    type="checkbox"
                    checked={m.assigned}
                    disabled={toggle.isPending}
                    onChange={(e) => toggle.mutate({ userId: m.user_id, assign: e.target.checked })}
                    style={{ width: 16, height: 16, cursor: "pointer" }}
                  />
                  <span style={{ flex: 1 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 500 }}>{m.display_name}</span>
                    <span style={{ fontSize: 12, color: "var(--mute)", marginLeft: 8 }}>{m.email}</span>
                  </span>
                  {m.designation && (
                    <span style={{ fontSize: 12, color: "var(--body-mid)" }}>{m.designation}</span>
                  )}
                  <span style={rolePill(m.role)}>{m.role}</span>
                </label>
              ))}
            </div>
          )}

          {/* Remove from tenant (pause). Re-assigning resumes the backlog. */}
          <div style={dangerRow}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Remove from tenant</div>
              <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 2 }}>
                Stops Atlas and hides the backlog (ticket statuses are kept).
                Re-assign later to resume from the remaining tickets.
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    `Remove ${selectedProject?.key} from ${selectedTenantName}? Atlas stops and the backlog is hidden until you re-assign it.`,
                  )
                ) {
                  unassign.mutate();
                }
              }}
              disabled={unassign.isPending}
              style={dangerBtn}
            >
              {unassign.isPending ? "Removing…" : "Unassign project"}
            </button>
          </div>
        </>
      )}
      {projectId && members.some((m) => m.role === "admin") && (
        <p style={{ fontSize: 12, color: "var(--mute)", marginTop: 10 }}>
          Note: Rooms is employee-only for now, so admins you assign won't see the
          room until that's opened up.
        </p>
      )}
    </div>
  );
}

const field: CSSProperties = { display: "flex", flexDirection: "column", gap: 6 };
const label: CSSProperties = { fontSize: 12.5, fontWeight: 500, color: "var(--body-mid)" };
const input: CSSProperties = {
  background: "var(--canvas)", border: "1px solid var(--hairline)", borderRadius: 6,
  padding: "9px 11px", fontSize: 14, color: "var(--ink)", outline: "none", minWidth: 240,
};
const row: CSSProperties = {
  display: "flex", alignItems: "center", gap: 12, padding: "11px 14px",
  borderBottom: "1px solid var(--hairline)", background: "var(--canvas)", cursor: "pointer",
};
const bindCard: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 14, alignItems: "flex-start",
  border: "1px solid var(--hairline)", borderRadius: 8, padding: 18, background: "var(--canvas)",
};
function primaryBtn(disabled: boolean): CSSProperties {
  return {
    background: "var(--ink)", color: "var(--on-primary)", border: "none", borderRadius: 4,
    padding: "10px 18px", fontSize: 14, fontWeight: 550,
    cursor: disabled ? "wait" : "pointer", opacity: disabled ? 0.6 : 1,
  };
}
function rolePill(role: string): CSSProperties {
  const admin = role === "admin";
  return {
    fontSize: 11, fontFamily: "var(--font-mono)", padding: "2px 8px", borderRadius: 3,
    background: admin ? "var(--ink)" : "var(--surface-2)",
    color: admin ? "var(--on-primary)" : "var(--body-mid)",
  };
}
const errBar: CSSProperties = {
  padding: "9px 12px", fontSize: 13, color: "#ee1d36", marginBottom: 14,
  background: "rgba(238,29,54,0.06)", border: "1px solid rgba(238,29,54,0.3)", borderRadius: 6,
};
const dangerRow: CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
  marginTop: 18, padding: "14px 16px", borderRadius: 8,
  border: "1px solid rgba(238,29,54,0.25)", background: "rgba(238,29,54,0.04)",
};
const dangerBtn: CSSProperties = {
  background: "var(--canvas)", border: "1px solid rgba(238,29,54,0.5)", borderRadius: 4,
  padding: "8px 14px", fontSize: 13, fontWeight: 500, color: "#c81e2e", cursor: "pointer",
  whiteSpace: "nowrap",
};
